import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

import { addOrRemoveLeagueToFavorites } from '@/utils/actions/leagues/addOrRemoveLeagueFavorite';

interface FavoriteButtonProps {
  moduleUuid: string;
  isFavorited: boolean;
  setIsFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  isDetailsPage?: boolean;
}

export const FavoriteButton = ({
  moduleUuid,
  isFavorited,
  setIsFavorited,
  isDetailsPage,
}: FavoriteButtonProps) => {
  const [localIsFavorited, setLocalIsFavorited] = useState(isFavorited);
  const [error, setError] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleFavoriteAction = useCallback(async () => {
    if (isDisabled) return;

    setError(null);
    setIsDisabled(true);

    const newFavoritedState = !localIsFavorited;
    setLocalIsFavorited(newFavoritedState);
    setIsFavorited(newFavoritedState);

    try {
      await addOrRemoveLeagueToFavorites({
        moduleFor: 'League',
        moduleUuid,
        favoriteStatus: newFavoritedState === true ? 1 : 0,
      });
    } catch {
      setError('Action failed. Please try again.');
      setLocalIsFavorited(!newFavoritedState);
      setIsFavorited(!newFavoritedState);
    } finally {
      setIsDisabled(false);
    }
  }, [isDisabled, localIsFavorited, moduleUuid, setIsFavorited]);

  return (
    <>
      <button
        onClick={(event) => {
          event.stopPropagation();
          handleFavoriteAction();
        }}
        disabled={isDisabled}
        className={clsx(
          'flex max-h-[34px] items-center justify-center rounded-md border border-gray-300 p-2 transition-all duration-200',
          {
            'cursor-not-allowed opacity-50': isDisabled,
            'bg-white hover:bg-gray-100': !isDisabled,
            'max-w-[34px]': !isDetailsPage,
            'max-h-none px-2 py-1 text-sm': isDetailsPage,
          }
        )}
      >
        <div className='flex flex-row items-center justify-center gap-2'>
          {isDetailsPage ? (
            <p className='hidden text-gray-700 sm:block'>
              {!localIsFavorited ? 'Add to favorites' : 'Favorited'}
            </p>
          ) : null}
          <FontAwesomeIcon
            icon={localIsFavorited ? faHeartSolid : faHeartRegular}
            size='lg'
            className={clsx(
              localIsFavorited
                ? isDetailsPage
                  ? 'text-leagues-300'
                  : 'text-error-500'
                : 'text-gray-700',
              {
                'animate-bounce': isDisabled,
              }
            )}
          />
        </div>
      </button>
      {error ? <p className='mt-1 text-sm text-error-500'>{error}</p> : null}
    </>
  );
};
